// PATH: src/theme.ts — jetons de design de l'application (source unique).
//
// Style « Data-Dense Dashboard » : l'écran d'une école est une console de
// travail (listes, tableaux, saisie), pas une page vitrine — on privilégie la
// densité lisible, un bleu professionnel et une échelle de gris froide qui
// s'accorde au bandeau latéral marine existant.
//
// Tout passe par les jetons antd : aucune couleur en dur dans les pages, pour
// qu'un changement de charte reste une modification d'un seul fichier.
import type { ThemeConfig } from "antd";

export const COLORS = {
  primary: "#2563EB",
  primaryHover: "#1D4ED8",
  accent: "#059669",
  danger: "#DC2626",
  warning: "#D97706",
  background: "#F6F8FC",
  surface: "#FFFFFF",
  foreground: "#0F172A",
  muted: "#64748B",
  border: "#E7EDF5",
  // Bandeau latéral : le marine du fond d'écran, plus sombre que le contenu.
  siderBg: "#0F172A",
  siderActive: "#2563EB",
} as const;

// Neutres du thème sombre. Les couleurs de MARQUE (bleu, vert, rouge, ambre) ne
// changent pas : seules les surfaces, le texte et les bordures s'inversent, de
// sorte que la charte reste reconnaissable d'un mode à l'autre.
//
// Ces valeurs sont indispensables : un jeton explicite l'emporte sur
// l'algorithme sombre d'antd. Laisser `colorText: #0F172A` figé dans la
// configuration donnait du texte noir sur fond noir — l'algorithme n'avait pas
// la main pour le corriger.
const DARK = {
  background: "#0B1120",
  surface: "#111A2E",
  foreground: "#E2E8F0",
  muted: "#94A3B8",
  border: "#1F2A3C",
  tableHeaderBg: "#16203A",
  rowHoverBg: "#16213A",
  subtleBg: "#1B2740",
  segmentedTrack: "#0E1830",
} as const;

// Équivalents clairs des surfaces secondaires, pour que les deux modes se
// lisent dans le même tableau de correspondance.
const LIGHT = {
  background: COLORS.background,
  surface: COLORS.surface,
  foreground: COLORS.foreground,
  muted: COLORS.muted,
  border: COLORS.border,
  tableHeaderBg: "#F1F5FB",
  rowHoverBg: "#F5F8FE",
  subtleBg: "#F1F5FB",
  segmentedTrack: "#EAF0F9",
} as const;

// Échelle d'ombres douces (« Soft UI ») — teintées ardoise, jamais noires : une
// ombre neutre pique l'œil sur un fond froid. Une seule source pour que cartes,
// menus et modales partagent la même profondeur.
export const SHADOWS = {
  xs: "0 1px 2px rgba(15,23,42,0.04)",
  sm: "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)",
  md: "0 4px 12px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)",
  lg: "0 12px 28px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.05)",
  // Halo de marque pour l'action primaire au survol.
  brand: "0 6px 16px rgba(37,99,235,0.24)",
} as const;

// Courbe et durée communes : tous les mouvements de l'app partagent ce rythme
// (cf. index.css). ease-out à l'entrée, discret, autour de 200 ms.
export const MOTION = { fast: "140ms", base: "200ms", ease: "cubic-bezier(0.22, 1, 0.36, 1)" } as const;

// Repli système derrière chaque police : le texte reste lisible même si le
// chargement Google Fonts échoue (réseau filtré, poste hors ligne).
export const FONT_STACK =
  "'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Données tabulaires : identifiants (code Massar), dates, montants. La chasse
// fixe aligne les colonnes et distingue 0/O et 1/l — indispensable sur un code
// qu'un agent recopie depuis un document officiel.
export const FONT_MONO = "'Fira Code', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/**
 * buildTheme — configuration antd pour un mode donné.
 *
 * Fonction et non constante : les jetons NEUTRES (texte, surfaces, bordures)
 * doivent suivre le mode. Une configuration figée les imposait à l'algorithme
 * sombre, qui ne pouvait alors rien corriger.
 */
export const buildTheme = (dark: boolean): ThemeConfig => {
  const c = dark ? DARK : LIGHT;
  return {
  token: {
    colorPrimary: COLORS.primary,
    colorInfo: COLORS.primary,
    colorSuccess: COLORS.accent,
    colorError: COLORS.danger,
    colorWarning: COLORS.warning,
    colorLink: COLORS.primary,
    colorLinkHover: COLORS.primaryHover,
    colorText: c.foreground,
    colorTextSecondary: c.muted,
    colorBgLayout: c.background,
    colorBgContainer: c.surface,
    colorBgElevated: c.surface,
    colorBorder: c.border,
    colorBorderSecondary: c.border,
    fontFamily: FONT_STACK,
    fontSize: 14,
    // Rayons un cran plus doux : la rondeur, tenue et régulière, est ce qui
    // sépare une console fonctionnelle d'un produit soigné.
    borderRadius: 10,
    borderRadiusLG: 14,
    borderRadiusSM: 8,
    // Profondeur pilotée par l'échelle commune (cartes, dropdowns, popovers).
    boxShadow: SHADOWS.md,
    boxShadowSecondary: SHADOWS.lg,
    // 36px : compact sans descendre sous la cible tactile confortable des
    // contrôles secondaires ; les boutons d'action restent en taille par défaut.
    controlHeight: 36,
    controlHeightLG: 42,
    wireframe: false,
  },
  components: {
    Layout: {
      // Le bandeau latéral est marine dans les DEUX modes : il sert de repère
      // de marque, et l'inverser en clair le ferait disparaître.
      siderBg: COLORS.siderBg,
      headerBg: c.surface,
      headerHeight: 60,
      headerPadding: "0 24px",
      bodyBg: c.background,
    },
    Menu: {
      darkItemBg: "transparent",
      darkSubMenuItemBg: "transparent",
      darkItemSelectedBg: COLORS.siderActive,
      darkItemHoverBg: "rgba(255,255,255,0.07)",
      darkItemColor: "#B9C4D4",
      darkItemHoverColor: "#FFFFFF",
      darkItemSelectedColor: "#FFFFFF",
      itemMarginInline: 10,
      itemHeight: 44,
      itemBorderRadius: 10,
      iconSize: 17,
      collapsedIconSize: 18,
    },
    Table: {
      headerBg: c.tableHeaderBg,
      headerColor: c.muted,
      headerSplitColor: "transparent",
      rowHoverBg: c.rowHoverBg,
      cellPaddingBlock: 13,
      borderColor: c.border,
      headerBorderRadius: 12,
    },
    Card: {
      headerBg: "transparent",
      headerFontSize: 15,
      borderRadiusLG: 14,
      boxShadowTertiary: SHADOWS.sm,
    },
    Tabs: {
      horizontalItemPadding: "12px 2px",
      horizontalItemGutter: 24,
      titleFontSize: 14,
      inkBarColor: COLORS.primary,
    },
    Tag: {
      defaultBg: c.subtleBg,
      defaultColor: c.muted,
      borderRadiusSM: 6,
    },
    Button: {
      primaryShadow: "none",
      defaultShadow: "none",
      fontWeight: 500,
      controlHeight: 36,
    },
    Segmented: {
      itemSelectedBg: c.surface,
      itemSelectedColor: c.foreground,
      trackBg: c.segmentedTrack,
      borderRadius: 10,
      borderRadiusSM: 8,
    },
    Input: { paddingBlock: 7, activeShadow: "0 0 0 3px rgba(37,99,235,0.12)" },
    Select: { borderRadius: 10 },
    Modal: { borderRadiusLG: 16 },
    Statistic: { titleFontSize: 13, contentFontSize: 26 },
  },
  };
};

/** Thème clair — conservé pour les rares usages hors ConfigProvider. */
export const theme: ThemeConfig = buildTheme(false);
